import React from 'react';
import {Box, CircularProgress, FormControl, Grid, TextField, useMediaQuery, useTheme, InputAdornment} from "@mui/material";
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Typography from "@mui/material/Typography";
import {DatePicker} from "@mui/x-date-pickers/DatePicker";
import dayjs, {Dayjs} from "dayjs";
import styles from './scoreCardDialog.module.css';
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import {NumericFormat} from 'react-number-format';
import Button from "@mui/material/Button";
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import IgolfTextfield from './igolfTextfield/igolfTextfield';
import {iGolfCourseTee} from "../../../interfaces/iGolfCourseTee";
import {iGolfCourse} from "../../../interfaces/iGolfCourse";
import {recordScore} from "../../../interfaces/recordScore";
import DateIconModal from "../../icons/dateIconmodal";

interface ScoreCardFormProps {
    dateValue: Dayjs | null;
    setDateValue: (value: Dayjs | null) => void;
    score: string;
    setScore: (value: string) => void;
    courseName: string;
    setCourseName: (value: string) => void;
    fairway: string;
    setFairway: (value: string) => void;
    greensInRegulation: string;
    setGreensInRegulation: (value: string) => void;
    numberOfPutts: string;
    setNumberOfPutts: (value: string) => void;
    upAndDowns: string;
    setUpAndDowns: (value: string) => void;
    chippings: string;
    setChippings: (value: string) => void;
    mental: string;
    setMental: (value: string) => void;
    strokes: string;
    setStrokes: (value: string) => void;
    holes: string;
    setHoles: (value: string) => void;
    expanded: boolean;
    setExpanded: (value: boolean) => void;
    numHoles: string;
    setNumHoles: (value: string) => void;
    iGolfTeeText: string;
    setiGolfTeeText: (value: string) => void;
    selectedCourse: iGolfCourse | null;
    setSelectedCourse: (value: iGolfCourse | null) => void;
    selectedTee: iGolfCourseTee | null;
    setSelectedTee: (value: iGolfCourseTee | null) => void;
    gender: string;
    setGender: (value: string) => void;
    courseList: iGolfCourse[];
    searchiGolfCourses: (courseName: string, stateList: any[]) => void;
    countryStateList: any[];
    teeDetails: iGolfCourseTee[];
    loadingiGolf: boolean;
    loadingTee: boolean;
    getTeeDetailsText: (tee: iGolfCourseTee) => string;
    handleTeeSelectChange: (event: SelectChangeEvent) => void;
    handleTeeChange: (tee: iGolfCourseTee) => void;
    setHolesByScore: (e: any) => void;
    valuesPopulated: () => boolean;
    submitScoreCard: () => void;
    loading: boolean;
    onCancel?: () => void;
    record?: recordScore;
    showDialog?: boolean;
    isInDialog?: boolean;
}

const ScoreCardForm: React.FC<ScoreCardFormProps> = ({
    dateValue,
    setDateValue,
    score,
    setScore,
    courseName,
    setCourseName,
    fairway,
    setFairway,
    greensInRegulation,
    setGreensInRegulation,
    numberOfPutts,
    setNumberOfPutts,
    upAndDowns,
    setUpAndDowns,
    chippings,
    setChippings,
    mental,
    setMental,
    strokes,
    setStrokes,
    holes,
    setHoles,
    expanded,
    setExpanded,
    numHoles,
    setNumHoles,
    iGolfTeeText,
    setiGolfTeeText,
    selectedCourse,
    setSelectedCourse,
    selectedTee,
    setSelectedTee,
    gender,
    setGender,
    courseList,
    searchiGolfCourses,
    countryStateList,
    teeDetails,
    loadingiGolf,
    loadingTee,
    getTeeDetailsText,
    handleTeeSelectChange,
    handleTeeChange,
    setHolesByScore,
    valuesPopulated,
    submitScoreCard,
    loading,
    onCancel,
    record,
    showDialog = true,
    isInDialog = true,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const dateInputRef = React.useRef<HTMLInputElement>(null);

    return (
        <FormControl>
            <Grid container spacing={3}>
                {isMobile && !isInDialog && 
                    <Typography 
                        sx={{fontSize: 36, 
                            fontWeight: 700, 
                            fontFamily: 'Repro', 
                            color: "#121212",
                            paddingLeft: '24px',
                        }}
                        >Submit Scoreboard</Typography>                        
                }

                <Grid item xs={12} md={12} sx={{display: "flex"}}>
                    <Box sx={{marginLeft: "auto"}} className={styles.photoLinks} onClick={() => window.open("/contact-us/")}>
                        <Typography sx={{margin: "auto 3px auto 0", fontSize: 16, fontWeight: 500, fontFamily: 'Repro', color: "#7B726C"}}>Need help?</Typography>
                        <HelpOutlineIcon sx={{color: "#7B726C"}}/>
                    </Box>
                </Grid>
                <Grid item xs={12} sx={{ 
                    '@media (min-width: 600px)': { 
                        maxWidth: '50%',
                        flexBasis: '50%',
                        flexGrow: 0
                    },
                    paddingTop: isMobile ? 0 : "10px"
                }}>
                    <Box className={styles.inputBox}>
                        <Typography className={styles.inputLabel}>
                            Date</Typography>
                        <DatePicker
                            slots={{ openPickerIcon: DateIconModal }}
                            value={dateValue}
                            onChange={(newValue) => setDateValue(newValue)}
                            sx={{
                                width: "100%",
                                backgroundColor: "#ffffff",
                                bottom: 0,
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        borderColor: '#ECE9E4',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: '#7b726c',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: '#7b726c',
                                    },
                                },
                                '& .MuiPickersActionBar-root': {
                                    '& .MuiButton-root': {
                                        color: '#7b726c',
                                        '&.MuiButton-textPrimary': {
                                            color: '#7b726c',
                                        },
                                    },
                                },
                            }}
                            slotProps={
                                {
                                    textField: {
                                        size: 'small',
                                        required: true,
                                        fullWidth: true,
                                        inputRef: dateInputRef,
                                        InputProps: {
                                            style: {
                                                height: "53px",
                                            },
                                            // endAdornment: (
                                            //     <InputAdornment position="end">
                                            //         <DateIconModal 
                                            //             onClick={(e: React.MouseEvent<HTMLElement>) => {
                                            //                 e.stopPropagation();
                                            //                 e.preventDefault();
                                            //                 if (dateInputRef.current) {
                                            //                     dateInputRef.current.focus();
                                            //                     dateInputRef.current.click();
                                            //                 } else {
                                            //                     const input = e.currentTarget.closest('.MuiOutlinedInput-root')?.querySelector('input') as HTMLInputElement;
                                            //                     if (input) {
                                            //                         input.focus();
                                            //                         input.click();
                                            //                     }
                                            //                 }
                                            //             }}
                                            //             sx={{cursor: "pointer"}}
                                            //         />
                                            //     </InputAdornment>
                                            // ),
                                        },
                                        sx: {
                                            width: "100%",
                                            '& .MuiOutlinedInput-root': {
                                                width: "100%",
                                                height: "53px",
                                                '& fieldset': {
                                                    borderColor: '#ECE9E4',
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: '#7b726c',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#7b726c',
                                                },
                                            },
                                            '& .MuiInputBase-input': {
                                                fontFamily: 'Repro Mono',
                                                color: '#7B726C',
                                            },
                                        }
                                    },
                                    calendarHeader: {
                                        sx: {
                                            '& .MuiPickersCalendarHeader-label': {
                                                color: '#7b726c',
                                            },
                                            '& .MuiIconButton-root': {
                                                color: '#FD3300',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(253, 51, 0, 0.1)',
                                                },
                                            },
                                        }
                                    },
                                    day: {
                                        sx: {
                                            '&.Mui-selected': {
                                                backgroundColor: '#FD3300',
                                                '&:hover': {
                                                    backgroundColor: '#DB2C00',
                                                },
                                                '&:focus': {
                                                    backgroundColor: '#FD3300',
                                                },
                                            },
                                            '&.MuiPickersDay-today': {
                                                border: '1px solid #FD3300',
                                            },
                                        }
                                    },
                                    actionBar: {
                                        sx: {
                                            '& .MuiButton-root': {
                                                color: '#FD3300',
                                                '&.MuiButton-textPrimary': {
                                                    color: '#FD3300',
                                                },
                                            },
                                        }
                                    },
                                }
                            }
                        />
                    </Box>
                </Grid>
                <Grid item xs={12} sx={{ 
                    '@media (min-width: 600px)': { 
                        maxWidth: '50%',
                        flexBasis: '50%',
                        flexGrow: 0
                    },
                    paddingTop: isMobile ? 0 : "10px"
                }}>
                    <IgolfTextfield record={record} setCourseName={setCourseName} searchiGolfCourses={searchiGolfCourses} countryStateList={countryStateList} selectedCourse={selectedCourse} setSelectedCourse={setSelectedCourse} options={courseList} loading={loadingiGolf} showDialog={showDialog} setiGolfTeeText={setiGolfTeeText} setSelectedTee={setSelectedTee} />
                </Grid>
                <Grid item xs={12} sx={{ 
                    '@media (min-width: 600px)': { 
                        maxWidth: '50%',
                        flexBasis: '50%',
                        flexGrow: 0
                    },
                    paddingTop: isMobile ? "20px" : "0"
                }}>
                    <Box className={`${styles.inputBox}`}>
                        <Typography className={styles.inputLabel}>
                            Tee
                        </Typography>
                        <Select
                            fullWidth
                            size="small"
                            displayEmpty
                            value={iGolfTeeText || ''}
                            required
                            disabled={!selectedCourse}
                            className={!selectedCourse ? styles.disabledTee : ''}
                            onChange={handleTeeSelectChange}
                            renderValue={(value) => {
                                if (selectedTee) {
                                    return getTeeDetailsText(selectedTee);
                                }
                                if (!value || value === '') {
                                    const placeholderColor = !selectedCourse ? '#ECE9E4' : '#7B726C';
                                    return <span style={{ color: placeholderColor }}>SELECT A TEE</span>;
                                }
                                return value;
                            }}
                            sx={{
                                '& .MuiInputBase-root': {
                                    height: '28px !important',
                                    minHeight: '28px !important',
                                },
                                '& .MuiOutlinedInput-root': {
                                    height: '28px !important',
                                    minHeight: '28px !important',
                                },
                                '& .MuiInputBase-input': {
                                    textTransform: 'uppercase',
                                    height: '28px !important',
                                    padding: '12.5px 14px !important',
                                    fontFamily: 'Repro Mono',
                                    color: '#7B726C',
                                },
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#ECE9E4',
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#7b726c',
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#7b726c',
                                },
                                '& .MuiSelect-icon': {
                                    color: '#7b726c',
                                },
                                '&.Mui-disabled': {
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#ECE9E4',
                                    },
                                    '& .MuiInputBase-input': {
                                        color: '#ECE9E4',
                                        WebkitTextFillColor: '#ECE9E4',
                                    },
                                    '& .MuiSelect-icon': {
                                        color: '#ECE9E4',
                                    },
                                },
                                '& .MuiPaper-root': {
                                    '& .MuiMenuItem-root': {
                                        '&.Mui-selected': {
                                            backgroundColor: 'rgba(253, 51, 0, 0.1)',
                                            '&:hover': {
                                                backgroundColor: 'rgba(253, 51, 0, 0.2)',
                                            },
                                        },
                                        '&:hover': {
                                            backgroundColor: 'rgba(253, 51, 0, 0.1)',
                                        },
                                    },
                                },
                            }}
                            MenuProps={{
                                PaperProps: {
                                    sx: {
                                        '& .MuiMenuItem-root': {
                                            '&.Mui-selected': {
                                                backgroundColor: 'rgba(253, 51, 0, 0.1)',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(253, 51, 0, 0.2)',
                                                },
                                            },
                                            '&:hover': {
                                                backgroundColor: 'rgba(253, 51, 0, 0.1)',
                                            },
                                        },
                                    },
                                },
                            }}
                        >
                            <Box sx={{ p: 1 }} className={styles.toggleButtonGroupContainer}>
                                <ToggleButtonGroup
                                    className={styles.toggleButtonGroup}
                                    value={gender}
                                    exclusive
                                    onChange={(e, newGender) => {
                                        if (newGender !== null) {
                                            e.stopPropagation();
                                            setGender(newGender);
                                        }
                                    }}
                                    onClick={(e: React.MouseEvent<HTMLElement>) => e.stopPropagation()}
                                    sx={{
                                        width: '100%',
                                        mb: 2,
                                        '& .MuiToggleButton-root': {
                                            flex: 1,
                                            '&.Mui-selected': {
                                                backgroundColor: 'white',
                                                color: '#fff',
                                            },
                                        },
                                    }}
                                >
                                    <ToggleButton value="Men">Men</ToggleButton>
                                    <ToggleButton value="Women">Women</ToggleButton>
                                </ToggleButtonGroup>
                            </Box>
                            {!loadingTee && gender && teeDetails &&
                                teeDetails.map((option) => (
                                <MenuItem
                                    classes={{
                                        root: styles.teeItem,
                                        selected: styles.selectedTee
                                    }}
                                    key={`iGolfTee_${option.displayOrder}`}
                                    value={getTeeDetailsText(option)}
                                    onClick={() => handleTeeChange(option)}
                                >
                                    {getTeeDetailsText(option)}
                                </MenuItem>
                            ))}
                            {loadingTee &&
                                <div className={`${styles.loadingiGolf} ${styles.iGolfLoading}`}>
                                    <CircularProgress size={'25px'} sx={{ color: '#FD3300' }}/>
                                </div>
                            }
                        </Select>
                    </Box>
                </Grid>
                <Grid item xs={12} sx={{ 
                    '@media (min-width: 600px)': { 
                        maxWidth: '50%',
                        flexBasis: '50%',
                        flexGrow: 0
                    },
                    paddingTop: "0"
                }}>
                    <Box className={styles.inputBox} sx={{
                            width: '100%',
                        }}>
                        <Box sx={{ width: '100%' }}>
                            <Typography className={styles.inputLabel}>Number of Holes</Typography>
                        </Box>
                        <Box sx={{ width: '100%' }}>
                            <RadioGroup
                                row
                                value={numHoles}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNumHoles(e.target.value)}
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    width: '100%',
                                    padding: '0 15px',
                                    paddingTop: '5px'
                                }}
                            >
                                <FormControlLabel 
                                    value="18" 
                                    control={
                                        <Radio 
                                            sx={{
                                                color: '#D9D9D9',
                                                '&.Mui-checked': {
                                                    color: '#FD3300',
                                                    '& .MuiSvgIcon-root': {
                                                        '& circle:first-of-type': {
                                                            stroke: '#D9D9D9',
                                                        },
                                                        '& circle:last-of-type': {
                                                            fill: '#FD3300',
                                                        },
                                                    },
                                                },
                                            }}
                                            size="small"
                                        />
                                    } 
                                    label={<Typography sx={{ fontSize: '16px', fontWeight: 400, fontFamily: 'Repro' }}>18 Holes</Typography>}
                                />
                                <FormControlLabel 
                                    value="front9"
                                    control={
                                        <Radio 
                                            sx={{
                                                color: '#D9D9D9',
                                                '&.Mui-checked': {
                                                    color: '#FD3300',
                                                    '& .MuiSvgIcon-root': {
                                                        '& circle:first-of-type': {
                                                            stroke: '#D9D9D9',
                                                        },
                                                        '& circle:last-of-type': {
                                                            fill: '#FD3300',
                                                        },
                                                    },
                                                },
                                            }}
                                            size="small"
                                        />
                                    } 
                                    label={<Typography sx={{ fontSize: '16px', fontWeight: 400, fontFamily: 'Repro' }}>Front 9</Typography>}
                                />
                                <FormControlLabel 
                                    value="back9" 
                                    control={
                                        <Radio 
                                            sx={{
                                                color: '#D9D9D9',
                                                '&.Mui-checked': {
                                                    color: '#FD3300',
                                                    '& .MuiSvgIcon-root': {
                                                        '& circle:first-of-type': {
                                                            stroke: '#D9D9D9',
                                                        },
                                                        '& circle:last-of-type': {
                                                            fill: '#FD3300',
                                                        },
                                                    },
                                                },
                                            }}
                                            size="small"
                                        />
                                    } 
                                    label={<Typography sx={{ fontSize: '16px', fontWeight: 400, fontFamily: 'Repro' }}>Back 9</Typography>}
                                />
                            </RadioGroup>
                        </Box>
                    </Box>
                </Grid>
                <Grid item xs={12} sx={{ 
                    '@media (min-width: 600px)': { 
                        maxWidth: '50%',
                        flexBasis: '50%',
                        flexGrow: 0
                    },
                    paddingTop: 0
                }}>
                    <Box className={styles.inputBox}>
                        <Typography className={styles.inputLabel}>
                            Total Score</Typography>
                        <NumericFormat
                            value={score}
                            onValueChange={(values) => setScore(values.value)}
                            onChange={(values) => setHolesByScore(values)}
                            customInput={TextField}
                            allowNegative={false}
                            decimalScale={0}
                            valueIsNumericString
                            variant="outlined"
                            size={"small"}
                            placeholder="Enter score"
                            fullWidth
                            required
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    height: '53px',
                                    '& fieldset': {
                                        borderColor: '#ECE9E4',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: '#7b726c',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: '#7b726c',
                                    },
                                },
                                '& .MuiInputBase-input': {
                                    fontFamily: 'Repro Mono',
                                    color: '#7B726C',
                                },
                                '& .MuiInputBase-input::placeholder': {
                                    textTransform: 'uppercase',
                                    opacity: 1,
                                },
                            }}
                        />
                    </Box>
                </Grid>
                <Grid item xs={12} sx={{ 
                    '@media (min-width: 600px)': { 
                        maxWidth: '50%',
                        flexBasis: '50%',
                        flexGrow: 0
                    },
                    paddingTop: 0
                }}>
                    <Box className={styles.inputBox}>
                        <Typography className={styles.inputLabel}>
                        How Many Greens in Regulation Did You Hit?</Typography>
                        <NumericFormat
                            value={greensInRegulation}
                            onValueChange={(values) => setGreensInRegulation(values.value)}
                            customInput={TextField}
                            allowNegative={false}
                            decimalScale={0}
                            valueIsNumericString
                            variant="outlined"
                            size={"small"}
                            placeholder="Enter number of greens"
                            fullWidth
                            required
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    height: '53px',
                                    '& fieldset': {
                                        borderColor: '#ECE9E4',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: '#7b726c0',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: '#7b726c',
                                    },
                                },
                                '& .MuiInputBase-input': {
                                    fontFamily: 'Repro Mono',
                                    color: '#7B726C',
                                },
                                '& .MuiInputBase-input::placeholder': {
                                    textTransform: 'uppercase',
                                    opacity: 1,
                                },
                            }}
                        />
                    </Box>
                </Grid>
                <Grid item xs={12} sx={{ 
                    '@media (min-width: 600px)': { 
                        maxWidth: '50%',
                        flexBasis: '50%',
                        flexGrow: 0
                    },
                    paddingTop: 0
                }}>
                    <Box className={styles.inputBox}>
                        <Typography className={styles.inputLabel}>
                        How Many Fairways Did You Hit?</Typography>
                        <NumericFormat
                            value={fairway}
                            customInput={TextField}
                            onValueChange={(values) => setFairway(values.value)}
                            allowNegative={false}
                            decimalScale={0}
                            valueIsNumericString
                            variant="outlined"
                            size={"small"}
                            placeholder="Enter number of fairways"
                            fullWidth
                            required
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    height: '53px',
                                    '& fieldset': {
                                        borderColor: '#ECE9E4',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: '#7b726c',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: '#7b726c',
                                    },
                                },
                                '& .MuiInputBase-input': {
                                    fontFamily: 'Repro Mono',
                                    color: '#7B726C',
                                },
                                '& .MuiInputBase-input::placeholder': {
                                    textTransform: 'uppercase',
                                    opacity: 1,
                                },
                            }}
                        />
                    </Box>
                </Grid>
                <Grid item xs={12} sx={{ 
                    '@media (min-width: 600px)': { 
                        maxWidth: '50%',
                        flexBasis: '50%',
                        flexGrow: 0
                    },
                    paddingTop: 0
                }}>
                    <Box className={styles.inputBox}>
                        <Typography className={styles.inputLabel}>
                        Total Number of Putts</Typography>
                        <NumericFormat
                            value={numberOfPutts}
                            onValueChange={(values) => setNumberOfPutts(values.value)}
                            customInput={TextField}
                            allowNegative={false}
                            decimalScale={0}
                            valueIsNumericString
                            variant="outlined"
                            size={"small"}
                            placeholder="Enter total number of putts"
                            fullWidth
                            required
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    height: '53px',
                                    '& fieldset': {
                                        borderColor: '#ECE9E4',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: '#7b726c',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: '#7b726c',
                                    },
                                },
                                '& .MuiInputBase-input': {
                                    fontFamily: 'Repro Mono',
                                    color: '#7B726C',
                                },
                                '& .MuiInputBase-input::placeholder': {
                                    textTransform: 'uppercase',
                                    opacity: 1,
                                },
                            }}
                        />
                    </Box>
                </Grid>
            </Grid>

            <Box sx={{
                padding: "12px 16px",
                backgroundColor: "#7B726C",
                border: "2px solid #7B726C",
                borderRadius: "8px",
                display: "flex",
                cursor: "pointer",
                marginBottom: "25px",
                marginTop: "20px"
            }} onClick={() => setExpanded(!expanded)}>
                <Typography sx={{fontSize: 16, lineHeight: "16px", fontWeight: 700, margin: "auto 0", textAlign: 'center', paddingLeft: '24px', width: '100%', color: '#fff', fontFamily: 'Repro'}}>
                Advanced Scorecard Tracking
                </Typography>
                {!expanded && <ExpandMoreIcon sx={{marginLeft: "auto", color: "#fff"}}/>}
                {expanded && <ExpandLessIcon sx={{marginLeft: "auto", color: "#fff"}}/>}
            </Box>

            {expanded &&
                <Grid container spacing={3}>
                    <Grid item xs={12} style={{paddingTop: 0}}>
                        <Box sx={{marginTop: "20px"}}>
                            <Typography sx={{fontSize: 16, lineHeight: "18px", fontWeight: 700}}>Chipping & Bunkers <span style={{fontSize: "12px"}}>(inside 30 yards)</span></Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={12} sx={{ 
                    '@media (min-width: 600px)': { 
                        maxWidth: '50%',
                        flexBasis: '50%',
                        flexGrow: 0
                    },
                    paddingTop: isMobile ? 0 : "10px"
                }}>
                        <Box className={styles.inputBox}>
                            <Typography className={styles.inputLabel}>
                                How many times did you get up & down in 2 shots or less?</Typography>
                            <NumericFormat
                                value={upAndDowns}
                                onValueChange={(values) => setUpAndDowns(values.value)}
                                customInput={TextField}
                                allowNegative={false}
                                decimalScale={0}
                                valueIsNumericString
                                variant="outlined"
                                size={"small"}
                                placeholder="Enter number of times"
                                fullWidth
                                required
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        height: '53px',
                                        '&:hover fieldset': {
                                            borderColor: '#7b726c',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#7b726c',
                                        },
                                    },
                                    '& .MuiInputBase-input': {
                                        fontFamily: 'Repro Mono',
                                        color: '#7B726C',
                                    },
                                    '& .MuiInputBase-input::placeholder': {
                                        textTransform: 'uppercase',
                                        opacity: 1,
                                    },
                                }}
                            />
                        </Box>
                    </Grid>
                    <Grid item xs={12} sx={{ 
                    '@media (min-width: 600px)': { 
                        maxWidth: '50%',
                        flexBasis: '50%',
                        flexGrow: 0
                    },
                    paddingTop: isMobile ? 0 : "10px"
                }}>
                        <Box className={styles.inputBox}>
                            <Typography className={styles.inputLabel} sx={{
                                '@media (min-width: 689px)': {
                                    paddingTop: '24px'
                                }
                            }}>
                                How many holes were you chipping on?</Typography>
                            <NumericFormat
                                value={chippings}
                                onValueChange={(values) => setChippings(values.value)}
                                customInput={TextField}
                                allowNegative={false}
                                decimalScale={0}
                                valueIsNumericString
                                variant="outlined"
                                size={"small"}
                                placeholder="Enter number of holes"
                                fullWidth
                                required
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        height: '53px',
                                        '&:hover fieldset': {
                                            borderColor: '#7b726c',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#7b726c',
                                        },
                                    },
                                    '& .MuiInputBase-input': {
                                        fontFamily: 'Repro Mono',
                                        color: '#7B726C',
                                    },
                                    '& .MuiInputBase-input::placeholder': {
                                        textTransform: 'uppercase',
                                        opacity: 1,
                                    },
                                }}
                            />
                        </Box>
                    </Grid>
                    <Grid item xs={12} style={{paddingTop: 0, marginTop: "20px"}}>
                        <Box sx={{marginTop: 0}}>
                            <Typography sx={{fontSize: 16, lineHeight: "18px", fontWeight: 700}}>Wedge
                                Game</Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={12} sx={{ 
                    '@media (min-width: 600px)': { 
                        maxWidth: '50%',
                        flexBasis: '50%',
                        flexGrow: 0
                    },
                    paddingTop: isMobile ? 0 : "10px"
                }}>
                        <Box className={styles.inputBox}>
                            <Typography className={styles.inputLabel}>
                            How many strokes did it take you to hole out from 30-100 yards?</Typography>
                            <NumericFormat
                                value={strokes}
                                onValueChange={(values) => setStrokes(values.value)}
                                customInput={TextField}
                                allowNegative={false}
                                decimalScale={0}
                                valueIsNumericString
                                variant="outlined"
                                size={"small"}
                                placeholder="Enter number of strokes"
                                fullWidth
                                required
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        height: '53px',
                                        '&:hover fieldset': {
                                            borderColor: '#7b726c',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#7b726c',
                                        },
                                    },
                                    '& .MuiInputBase-input': {
                                        fontFamily: 'Repro Mono',
                                        color: '#7B726C',
                                    },
                                    '& .MuiInputBase-input::placeholder': {
                                        textTransform: 'uppercase',
                                        opacity: 1,
                                    },
                                }}
                            />
                        </Box>
                    </Grid>
                    <Grid item xs={12} sx={{ 
                    '@media (min-width: 600px)': { 
                        maxWidth: '50%',
                        flexBasis: '50%',
                        flexGrow: 0
                    },
                    paddingTop: isMobile ? 0 : "10px"
                }}>
                        <Box className={styles.inputBox}>
                            <Typography className={styles.inputLabel} sx={{
                                '@media (min-width: 752px)': {
                                    paddingTop: '24px'
                                }
                            }}>
                                How many holes were you within this range?</Typography>
                            <NumericFormat
                                value={holes}
                                onValueChange={(values) => setHoles(values.value)}
                                customInput={TextField}
                                allowNegative={false}
                                decimalScale={0}
                                valueIsNumericString
                                variant="outlined"
                                size={"small"}
                                placeholder="Enter number of holes"
                                fullWidth
                                required
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        height: '53px',
                                        '&:hover fieldset': {
                                            borderColor: '#7b726c',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#7b726c',
                                        },
                                    },
                                    '& .MuiInputBase-input': {
                                        fontFamily: 'Repro Mono',
                                        color: '#7B726C',
                                    },
                                    '& .MuiInputBase-input::placeholder': {
                                        textTransform: 'uppercase',
                                        opacity: 1,
                                    },
                                }}
                            />
                        </Box>
                    </Grid>
                    <Grid item xs={12} sx={{ 
                    '@media (min-width: 600px)': { 
                        maxWidth: '50%',
                        flexBasis: '50%',
                        flexGrow: 0
                    },
                    paddingTop: isMobile ? 0 : "10px"
                }}>
                        <Box sx={{display: "flex", justifyContent: 'space-between'}}>
                            <Typography
                                sx={{margin: "auto 0", fontSize: 16, lineHeight: "18px", fontWeight: 700}}>
                                Mental Game</Typography>
                            <Box className={styles.photoLinks}>
                            </Box>
                        </Box>
                        <Box className={styles.inputBox} style={{paddingTop: "10px"}}>
                            <Typography className={styles.inputLabel}>
                                How many shots did you commit to?</Typography>
                            <NumericFormat
                                value={mental}
                                onValueChange={(values) => setMental(values.value)}
                                customInput={TextField}
                                allowNegative={false}
                                decimalScale={0}
                                valueIsNumericString
                                variant="outlined"
                                size={"small"}
                                placeholder="Enter number of shots"
                                fullWidth
                                required
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        height: '53px',
                                        '&:hover fieldset': {
                                            borderColor: '#7b726c',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#7b726c',
                                        },
                                    },
                                    '& .MuiInputBase-input': {
                                        fontFamily: 'Repro Mono',
                                        color: '#7B726C',
                                    },
                                    '& .MuiInputBase-input::placeholder': {
                                        textTransform: 'uppercase',
                                        opacity: 1,
                                    },
                                }}
                            />
                        </Box>
                    </Grid>
                </Grid>

            }
             <Grid item xs={12} sx={{ display: 'flex', gap: '15px', margin: "20px 0" }}>
                <Button
                    className={styles.outlinedButton}
                    disableRipple
                    onClick={onCancel}
                    variant="text"
                    sx={{
                        width: '50%',
                        height: '48px',
                        border: '1.5px solid #000000',
                        fontFamily: 'Repro',
                        fontSize: '16px',
                        fontWeight: 700,
                        color: '#000000 !important',
                        borderRadius: '100px',
                        textTransform: 'capitalize !important',
                        '&:hover': {
                            backgroundColor: '#F4F2F0 !important',
                        },
                    }}
                >Cancel</Button>
                <Button
                    type={"submit"}
                    onClick={() => {
                        submitScoreCard();
                    }}
                    disabled={!valuesPopulated() || loading}
                    disableRipple
                    className={styles.button}
                    sx={{
                        width: '50%',
                        height: '48px',
                        borderRadius: '100px',
                        backgroundColor: '#FD3300 !important',
                        color: '#fff !important',
                        fontFamily: 'Repro',
                        fontSize: '16px',
                        fontWeight: 700,
                        textTransform: 'capitalize !important',
                        '&:hover': {
                            backgroundColor: '#DB2C00 !important',
                        },
                        '&:disabled': {
                            backgroundColor: '#D9D9D9 !important',
                            color: '#fff !important',
                        },
                    }}
                    variant="contained">Submit</Button>
            </Grid>
        </FormControl>
    );
};

export default ScoreCardForm;

